import { CSSProperties, FC, forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Icon,
  Tooltip,
  Typography,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import clsx from 'clsx';
import { getEmployeeNumber } from 'src/store/user/selectors';
import { Desk } from 'src/types';

import classes from './DeskPlace.module.scss';

const borderRadiusCoef = 0.15;
const iconCoef = 0.8;

export interface DeskPlaceProps {
  item: Desk;
}

const DeskPlaceLayout = forwardRef<HTMLDivElement, DeskPlaceProps>(
  function Inner({ item }, ref) {
    const employeeNumber = useSelector(getEmployeeNumber);
    const { width, height, fontSize, rotation } = item;

    const x = item.x - width / 2;
    const y = item.y - height / 2;

    const containerStyle = useMemo(
      (): CSSProperties => ({
        transform: `translate(${x}px,${y}px)`,
        width: `${width}px`,
        height: `${height}px`,
      }),
      [height, width, x, y],
    );

    const borderRadius = borderRadiusCoef * width;
    const fontSizePx = `${fontSize}px`;
    const titleStyle = useMemo(
      (): CSSProperties => ({
        fontSize: fontSizePx,
      }),
      [fontSizePx],
    );
    const deskStyle = useMemo(
      (): CSSProperties => ({
        borderRadius: `${borderRadius}px`,
        transform: `rotate(${rotation}deg)`,
      }),
      [borderRadius, rotation],
    );
    const iconSize = `${iconCoef * width}px`;
    const iconStyle = useMemo(
      (): CSSProperties => ({
        fontSize: iconSize,
        width: iconSize,
        height: iconSize,
        lineHeight: iconSize,
      }),
      [iconSize],
    );
    const bookedByMe =
      !!employeeNumber &&
      item.status?.responsibleEmployeeNumber === employeeNumber;

    return (
      <div className={classes.container} style={containerStyle} ref={ref}>
        <div className={classes.figure}>
          <div
            className={clsx(classes.desk, bookedByMe && classes.bookedByMe)}
            style={deskStyle}
          />
          <div className={classes.content}>
            {bookedByMe ? (
              <Icon
                className={classes.text}
                style={iconStyle}
                iconName={Icons.User}
              />
            ) : (
              <Typography
                className={classes.text}
                variant='body3'
                style={titleStyle}
              >
                {item.title}
              </Typography>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export const DeskPlace: FC<DeskPlaceProps> = ({ item }) => {
  const employeeNumber = useSelector(getEmployeeNumber);
  const bookedByMe =
    !!employeeNumber &&
    item.status?.responsibleEmployeeNumber === employeeNumber;

  return bookedByMe ? (
    <Tooltip title='Твоё рабочее место' triggerMode='hover'>
      <DeskPlaceLayout item={item} />
    </Tooltip>
  ) : (
    <DeskPlaceLayout item={item} />
  );
};
